import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { theme as appTheme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    DEFAULT_CAMPAIGN_DEADLINES,
    DEFAULT_CAMPAIGN_PLATFORMS,
    DEFAULT_CREATOR_PAYOUT_FEE,
    DEFAULT_DELIVERABLE_TYPES,
    DEFAULT_DURATION_RANGES,
    DEFAULT_INFLUENCER_TIERS,
    DEFAULT_SEAT_INCREASE_FEE_CENTS,
    DEFAULT_UGC_BUDGET,
} from '../../constants/ugcCampaignConfigDefaults';
import type { IGlobalConfigurationUpdate } from '../../redux/configuration/types';
import type {
    ICampaignDeadlinesConfig,
    ICampaignPlatformConfig,
    IDeliverableTypeConfig,
    IDurationRangeConfig,
    IInfluencerTierConfig,
} from '../../types/global/ugcCampaignConfig.types';
import {
    normalizeCampaignDeadlines,
    normalizeInfluencerTiers,
    normalizeCreatorPayoutFee,
    normalizeUgcBudget,
} from '../../utils/ugcCampaignConfig';
import { enrichContentScrubForEditor } from '../../utils/contentScrubUtils';
import { enrichBuzzbridgeLoyaltyForEditor } from './globalConfigurationBuzzbridgeLoyaltyEditor';

export function enrichGlobalConfigForEditor(source: IGlobalConfigurationUpdate): IGlobalConfigurationUpdate {
    const next: IGlobalConfigurationUpdate = JSON.parse(JSON.stringify(source));
    next.influencer_tiers = normalizeInfluencerTiers(
        next.influencer_tiers?.length ? next.influencer_tiers : DEFAULT_INFLUENCER_TIERS,
    );
    const buzz = next.buzzbridge ?? {
        enableAutoLink: { Instagram: true, YouTube: false, TikTok: false },
    };
    next.buzzbridge = {
        ...buzz,
        enableAutoLink: buzz.enableAutoLink ?? { Instagram: true, YouTube: false, TikTok: false },
        instagram_comment_automation_enabled: buzz.instagram_comment_automation_enabled ?? false,
        instagram_login_enabled: buzz.instagram_login_enabled ?? false,
        campaign_platforms:
            buzz.campaign_platforms && buzz.campaign_platforms.length > 0
                ? buzz.campaign_platforms
                : [...DEFAULT_CAMPAIGN_PLATFORMS],
        ugc_budget: normalizeUgcBudget(buzz.ugc_budget ?? DEFAULT_UGC_BUDGET),
        creator_payout: normalizeCreatorPayoutFee(buzz.creator_payout ?? DEFAULT_CREATOR_PAYOUT_FEE),
        campaign_deadlines: normalizeCampaignDeadlines(buzz.campaign_deadlines ?? DEFAULT_CAMPAIGN_DEADLINES),
        seat_increase_fee_cents:
            typeof buzz.seat_increase_fee_cents === 'number' &&
            Number.isFinite(buzz.seat_increase_fee_cents) &&
            buzz.seat_increase_fee_cents >= 0
                ? Math.round(buzz.seat_increase_fee_cents)
                : DEFAULT_SEAT_INCREASE_FEE_CENTS,
        deliverable_types:
            buzz.deliverable_types && buzz.deliverable_types.length > 0
                ? buzz.deliverable_types
                : [...DEFAULT_DELIVERABLE_TYPES],
        duration_ranges:
            buzz.duration_ranges && buzz.duration_ranges.length > 0
                ? buzz.duration_ranges
                : [...DEFAULT_DURATION_RANGES],
    };
    next.content_scrub = enrichContentScrubForEditor(next.content_scrub);
    next.buzzbridge_loyalty = enrichBuzzbridgeLoyaltyForEditor(next.buzzbridge_loyalty);
    return next;
}

const accordionSx = (themeStyle: keyof typeof appTheme) => ({
    backgroundColor: 'transparent',
    color: appTheme[themeStyle].tag_white,
    '& .MuiAccordionSummary-root': { color: appTheme[themeStyle].tag_white },
    '& .MuiAccordionDetails-root': { color: appTheme[themeStyle].tag_white },
});

const fieldSx = (themeStyle: keyof typeof appTheme) => ({
    '& .MuiOutlinedInput-root': {
        color: appTheme[themeStyle].tag_white,
        '& fieldset': { borderColor: appTheme[themeStyle].form_divider },
        '&:hover fieldset': { borderColor: appTheme[themeStyle].highlight },
    },
});

function parseUsd(value: string): number | null {
    const n = parseFloat(value.replace(/[$,]/g, '').trim());
    return Number.isFinite(n) ? n : null;
}

function parseRate(value: string): number | null {
    const n = parseFloat(value.replace(/%/g, '').trim());
    if (!Number.isFinite(n)) return null;
    return n > 1 ? n / 100 : n;
}

function isRateDraftAllowed(value: string): boolean {
    const trimmed = value.trim();
    if (trimmed === '') return true;
    return /^\d*\.?\d*%?$/.test(trimmed);
}

function DecimalRateField({
    value,
    onCommit,
    themeStyle,
}: {
    value: number;
    onCommit: (rate: number) => void;
    themeStyle: keyof typeof appTheme;
}) {
    const [draft, setDraft] = React.useState(String(value));

    React.useEffect(() => {
        setDraft(String(value));
    }, [value]);

    const commitDraft = () => {
        const trimmed = draft.trim();
        if (trimmed === '') {
            setDraft(String(value));
            return;
        }
        const rate = parseRate(trimmed);
        if (rate == null || rate < 0 || rate > 1) {
            setDraft(String(value));
            return;
        }
        onCommit(rate);
        setDraft(String(rate));
    };

    return (
        <TextField
            value={draft}
            size="small"
            onChange={e => {
                const next = e.target.value;
                if (!isRateDraftAllowed(next)) return;
                setDraft(next);
            }}
            onBlur={commitDraft}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDraft();
                    (e.target as HTMLInputElement).blur();
                }
            }}
            sx={{ ...fieldSx(themeStyle), width: '100%' }}
        />
    );
}

export interface GlobalConfigurationUgcEditorProps {
    config: IGlobalConfigurationUpdate;
    setConfig: React.Dispatch<React.SetStateAction<IGlobalConfigurationUpdate>>;
    setHasChanges: (changed: boolean) => void;
    themeStyle: keyof typeof appTheme;
}

export function GlobalConfigurationUgcEditor({
    config,
    setConfig,
    setHasChanges,
    themeStyle,
}: GlobalConfigurationUgcEditorProps) {
    const tiers = config.influencer_tiers ?? [];
    const platforms = config.buzzbridge?.campaign_platforms ?? [];
    const ugcBudget = config.buzzbridge?.ugc_budget ?? DEFAULT_UGC_BUDGET;
    const creatorPayout = config.buzzbridge?.creator_payout ?? DEFAULT_CREATOR_PAYOUT_FEE;
    const campaignDeadlines = normalizeCampaignDeadlines(
        config.buzzbridge?.campaign_deadlines ?? DEFAULT_CAMPAIGN_DEADLINES,
    );
    const seatIncreaseFeeCents =
        typeof config.buzzbridge?.seat_increase_fee_cents === 'number' &&
        Number.isFinite(config.buzzbridge.seat_increase_fee_cents) &&
        config.buzzbridge.seat_increase_fee_cents >= 0
            ? Math.round(config.buzzbridge.seat_increase_fee_cents)
            : DEFAULT_SEAT_INCREASE_FEE_CENTS;
    const deliverableTypes = config.buzzbridge?.deliverable_types ?? DEFAULT_DELIVERABLE_TYPES;
    const durationRanges = config.buzzbridge?.duration_ranges ?? DEFAULT_DURATION_RANGES;

    const updateTiers = (newTiers: IInfluencerTierConfig[]) => {
        setConfig({ ...config, influencer_tiers: newTiers });
        setHasChanges(true);
    };

    const updateCreatorPayout = (patch: Partial<typeof creatorPayout>) => {
        setConfig({
            ...config,
            buzzbridge: {
                ...config.buzzbridge,
                enableAutoLink: config.buzzbridge?.enableAutoLink ?? {
                    Instagram: true,
                    YouTube: false,
                    TikTok: false,
                },
                creator_payout: { ...creatorPayout, ...patch },
            },
        });
        setHasChanges(true);
    };

    const updateCampaignDeadlines = (patch: Partial<ICampaignDeadlinesConfig>) => {
        setConfig({
            ...config,
            buzzbridge: {
                ...config.buzzbridge,
                enableAutoLink: config.buzzbridge?.enableAutoLink ?? {
                    Instagram: true,
                    YouTube: false,
                    TikTok: false,
                },
                campaign_deadlines: normalizeCampaignDeadlines({
                    ...campaignDeadlines,
                    ...patch,
                }),
            },
        });
        setHasChanges(true);
    };

    const updateSeatIncreaseFeeCents = (cents: number) => {
        setConfig({
            ...config,
            buzzbridge: {
                ...config.buzzbridge,
                enableAutoLink: config.buzzbridge?.enableAutoLink ?? {
                    Instagram: true,
                    YouTube: false,
                    TikTok: false,
                },
                seat_increase_fee_cents: Math.max(0, Math.round(cents)),
            },
        });
        setHasChanges(true);
    };

    const updatePlatforms = (newPlatforms: ICampaignPlatformConfig[]) => {
        setConfig({
            ...config,
            buzzbridge: {
                ...config.buzzbridge,
                enableAutoLink: config.buzzbridge?.enableAutoLink ?? {
                    Instagram: true,
                    YouTube: false,
                    TikTok: false,
                },
                campaign_platforms: newPlatforms,
                ugc_budget: ugcBudget,
            },
        });
        setHasChanges(true);
    };

    const updateUgcBudget = (patch: Partial<typeof ugcBudget>) => {
        setConfig({
            ...config,
            buzzbridge: {
                ...config.buzzbridge,
                enableAutoLink: config.buzzbridge?.enableAutoLink ?? {
                    Instagram: true,
                    YouTube: false,
                    TikTok: false,
                },
                campaign_platforms: platforms,
                ugc_budget: { ...ugcBudget, ...patch },
            },
        });
        setHasChanges(true);
    };

    const updateDeliverableTypes = (next: IDeliverableTypeConfig[]) => {
        setConfig({ ...config, buzzbridge: { ...config.buzzbridge, deliverable_types: next } });
        setHasChanges(true);
    };

    const updateDurationRanges = (next: IDurationRangeConfig[]) => {
        setConfig({ ...config, buzzbridge: { ...config.buzzbridge, duration_ranges: next } });
        setHasChanges(true);
    };

    const [newDeliverable, setNewDeliverable] = useState({ id: '', label: '', platform: '' });
    const [newDeliverableError, setNewDeliverableError] = useState('');

    const addDeliverableType = () => {
        const id = newDeliverable.id.trim().toLowerCase().replace(/\s+/g, '_');
        const label = newDeliverable.label.trim();
        if (!id) {
            setNewDeliverableError('ID is required');
            return;
        }
        if (!label) {
            setNewDeliverableError('Label is required');
            return;
        }
        if (deliverableTypes.some(d => d.id === id)) {
            setNewDeliverableError(`"${id}" already exists`);
            return;
        }
        updateDeliverableTypes([
            ...deliverableTypes,
            {
                id,
                label,
                platform: newDeliverable.platform.trim() || undefined,
                enabled: true,
                sort_order: deliverableTypes.length,
            },
        ]);
        setNewDeliverable({ id: '', label: '', platform: '' });
        setNewDeliverableError('');
    };

    return (
        <>
            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        Creator payout platform fee
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Decimal rates (0.30 = 30%). Applied when paying creators from campaign escrow.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <LabeledField label="Non-subscriber rate" themeStyle={themeStyle}>
                            <DecimalRateField
                                value={creatorPayout.platform_fee_rate}
                                themeStyle={themeStyle}
                                onCommit={rate => updateCreatorPayout({ platform_fee_rate: rate })}
                            />
                        </LabeledField>
                        <LabeledField label="Subscriber rate (optional waiver)" themeStyle={themeStyle}>
                            <DecimalRateField
                                value={creatorPayout.platform_fee_rate_subscriber}
                                themeStyle={themeStyle}
                                onCommit={rate => updateCreatorPayout({ platform_fee_rate_subscriber: rate })}
                            />
                        </LabeledField>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={creatorPayout.waive_fee_when_host_referred === 'active'}
                                    onChange={(_, checked) =>
                                        updateCreatorPayout({
                                            waive_fee_when_host_referred: checked ? 'active' : 'inactive',
                                        })
                                    }
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: appTheme[themeStyle].highlight,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: appTheme[themeStyle].highlight,
                                        },
                                    }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].tag_white,
                                            fontSize: '0.875rem',
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        Waive fee when host was referred
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: appTheme[themeStyle].grey,
                                            fontSize: '0.75rem',
                                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                                        }}>
                                        When active, creators on a campaign whose host was BuzzBridge-referred get the
                                        subscriber fee rate even without a subscription.
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        Seat increase fee
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Flat platform fee charged from the brand wallet when they add creator seats. Escrow for the new
                        seats is separate and still required.
                    </Typography>
                    <LabeledField label="Fee per seat-increase request (USD)" themeStyle={themeStyle}>
                        <TextField
                            size="small"
                            placeholder="20.00"
                            defaultValue={(seatIncreaseFeeCents / 100).toFixed(2)}
                            key={seatIncreaseFeeCents}
                            onBlur={e => {
                                const usd = parseUsd(e.target.value);
                                if (usd == null || usd < 0) return;
                                updateSeatIncreaseFeeCents(Math.round(usd * 100));
                            }}
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ color: appTheme[themeStyle].grey, mr: 0.5, fontSize: 14 }}>
                                        $
                                    </Typography>
                                ),
                            }}
                            sx={fieldSx(themeStyle)}
                        />
                    </LabeledField>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        Campaign deadlines
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Per-country countdown windows (days). Brands get one reminder email per day during each window.
                        Applies to campaigns whose location matches this country config.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                        <LabeledField label="Auto-approve content (days)" themeStyle={themeStyle}>
                            <TextField
                                type="number"
                                size="small"
                                inputProps={{ min: 1, max: 90 }}
                                value={campaignDeadlines.auto_approve_days}
                                onChange={e => {
                                    const n = parseInt(e.target.value, 10);
                                    if (!Number.isFinite(n)) return;
                                    updateCampaignDeadlines({ auto_approve_days: n });
                                }}
                                sx={{ ...fieldSx(themeStyle), width: '100%' }}
                            />
                        </LabeledField>
                        <LabeledField label="Mark-complete / auto-payout (days)" themeStyle={themeStyle}>
                            <TextField
                                type="number"
                                size="small"
                                inputProps={{ min: 1, max: 90 }}
                                value={campaignDeadlines.mark_complete_deadline_days}
                                onChange={e => {
                                    const n = parseInt(e.target.value, 10);
                                    if (!Number.isFinite(n)) return;
                                    updateCampaignDeadlines({ mark_complete_deadline_days: n });
                                }}
                                sx={{ ...fieldSx(themeStyle), width: '100%' }}
                            />
                        </LabeledField>
                        <LabeledField label="Pre-end reminder days (comma-separated)" themeStyle={themeStyle}>
                            <PreEndReminderDaysField
                                value={campaignDeadlines.pre_end_reminder_days}
                                themeStyle={themeStyle}
                                onCommit={days => updateCampaignDeadlines({ pre_end_reminder_days: days })}
                            />
                        </LabeledField>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        UGC influencer tiers
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Used by the brand campaign wizard for tier cards and budget breakdown. Amounts are USD dollars
                        (not cents).
                    </Typography>
                    <Stack spacing={3}>
                        {tiers.map((tier, idx) => (
                            <Box
                                key={tier.id}
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    border: `1px solid ${appTheme[themeStyle].form_divider}`,
                                }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1.5,
                                    }}>
                                    <Typography
                                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: appTheme[themeStyle].tag_white }}>
                                        {tier.name} ({tier.id})
                                    </Typography>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={tier.active !== false}
                                                onChange={(_, checked) => {
                                                    const newTiers = [...tiers];
                                                    newTiers[idx] = { ...tier, active: checked };
                                                    updateTiers(newTiers);
                                                }}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                        color: appTheme[themeStyle].highlight,
                                                    },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                        backgroundColor: appTheme[themeStyle].highlight,
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                sx={{ color: appTheme[themeStyle].tag_white, fontSize: '0.8rem' }}>
                                                Active
                                            </Typography>
                                        }
                                    />
                                </Box>
                                <Box
                                    sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                                    <LabeledField label="Min followers" themeStyle={themeStyle}>
                                        <TextField
                                            value={tier.min_followers}
                                            size="small"
                                            onChange={e => {
                                                const num = parseInt(e.target.value, 10);
                                                if (isNaN(num)) return;
                                                const newTiers = [...tiers];
                                                newTiers[idx] = { ...tier, min_followers: num };
                                                updateTiers(newTiers);
                                            }}
                                            sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                        />
                                    </LabeledField>
                                    <LabeledField label="Reach label" themeStyle={themeStyle}>
                                        <TextField
                                            value={tier.reach}
                                            size="small"
                                            onChange={e => {
                                                const newTiers = [...tiers];
                                                newTiers[idx] = { ...tier, reach: e.target.value };
                                                updateTiers(newTiers);
                                            }}
                                            sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                        />
                                    </LabeledField>
                                    <LabeledField label="Min payout / creator ($)" themeStyle={themeStyle}>
                                        <TextField
                                            value={tier.min_payout_per_creator_usd}
                                            size="small"
                                            onChange={e => {
                                                const num = parseUsd(e.target.value);
                                                if (num == null) return;
                                                const newTiers = [...tiers];
                                                newTiers[idx] = { ...tier, min_payout_per_creator_usd: num };
                                                updateTiers(newTiers);
                                            }}
                                            sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                        />
                                    </LabeledField>
                                    <LabeledField label="Platform fee / platform ($)" themeStyle={themeStyle}>
                                        <TextField
                                            value={tier.platform_fee_per_platform_usd}
                                            size="small"
                                            onChange={e => {
                                                const num = parseUsd(e.target.value);
                                                if (num == null) return;
                                                const newTiers = [...tiers];
                                                newTiers[idx] = { ...tier, platform_fee_per_platform_usd: num };
                                                updateTiers(newTiers);
                                            }}
                                            sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                        />
                                    </LabeledField>
                                    <LabeledField
                                        label="Min payout / creator — subscribed host ($)"
                                        themeStyle={themeStyle}>
                                        <TextField
                                            value={tier.min_payout_per_creator_usd_subscribed ?? ''}
                                            size="small"
                                            placeholder="Optional"
                                            onChange={e => {
                                                const raw = e.target.value.trim();
                                                const newTiers = [...tiers];
                                                if (!raw) {
                                                    newTiers[idx] = {
                                                        ...tier,
                                                        min_payout_per_creator_usd_subscribed: undefined,
                                                    };
                                                } else {
                                                    const num = parseUsd(raw);
                                                    if (num == null) return;
                                                    newTiers[idx] = {
                                                        ...tier,
                                                        min_payout_per_creator_usd_subscribed: num,
                                                    };
                                                }
                                                updateTiers(newTiers);
                                            }}
                                            sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                        />
                                    </LabeledField>
                                    <LabeledField label="Sort order" themeStyle={themeStyle}>
                                        <TextField
                                            value={tier.sort_order ?? idx}
                                            size="small"
                                            onChange={e => {
                                                const num = parseInt(e.target.value, 10);
                                                if (isNaN(num)) return;
                                                const newTiers = [...tiers];
                                                newTiers[idx] = { ...tier, sort_order: num };
                                                updateTiers(newTiers);
                                            }}
                                            sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                        />
                                    </LabeledField>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        UGC campaign platforms
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Controls which platforms appear in the brand wizard. “Coming soon” shows a locked chip when not
                        enabled.
                    </Typography>
                    <Stack spacing={2}>
                        {platforms.map((platform, idx) => (
                            <Box
                                key={platform.id}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 1,
                                    borderBottom: `1px solid ${appTheme[themeStyle].form_divider}`,
                                }}>
                                <Typography
                                    sx={{
                                        minWidth: 120,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: appTheme[themeStyle].tag_white,
                                    }}>
                                    {platform.label}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={platform.enabled === true}
                                            onChange={(_, checked) => {
                                                const next = [...platforms];
                                                next[idx] = { ...platform, enabled: checked };
                                                updatePlatforms(next);
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: appTheme[themeStyle].tag_white }}>Enabled</Typography>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={platform.coming_soon === true}
                                            onChange={(_, checked) => {
                                                const next = [...platforms];
                                                next[idx] = { ...platform, coming_soon: checked };
                                                updatePlatforms(next);
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: appTheme[themeStyle].tag_white }}>
                                            Coming soon
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={platform.oauth_required === true}
                                            onChange={(_, checked) => {
                                                const next = [...platforms];
                                                next[idx] = { ...platform, oauth_required: checked };
                                                updatePlatforms(next);
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: appTheme[themeStyle].tag_white }}>
                                            OAuth required
                                        </Typography>
                                    }
                                />
                            </Box>
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        UGC budget rules
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack spacing={2}>
                        {[
                            {
                                label: 'Absolute min total budget ($)',
                                value: ugcBudget.absolute_min_total_budget_usd,
                                onChange: (n: number) => updateUgcBudget({ absolute_min_total_budget_usd: n }),
                            },
                            {
                                label: 'Default min participants',
                                value: ugcBudget.default_min_participants,
                                onChange: (n: number) => updateUgcBudget({ default_min_participants: n }),
                                integer: true,
                            },
                            {
                                label: 'Comfort budget multiplier',
                                value: ugcBudget.suggested_budget_multiplier_comfort,
                                onChange: (n: number) => updateUgcBudget({ suggested_budget_multiplier_comfort: n }),
                            },
                            {
                                label: 'Strong budget multiplier',
                                value: ugcBudget.suggested_budget_multiplier_strong,
                                onChange: (n: number) => updateUgcBudget({ suggested_budget_multiplier_strong: n }),
                            },
                        ].map(row => (
                            <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography
                                    sx={{
                                        color: appTheme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        minWidth: 220,
                                    }}>
                                    {row.label}
                                </Typography>
                                <TextField
                                    value={row.value}
                                    size="small"
                                    onChange={e => {
                                        const num = row.integer
                                            ? parseInt(e.target.value, 10)
                                            : parseFloat(e.target.value);
                                        if (!Number.isFinite(num)) return;
                                        row.onChange(num);
                                    }}
                                    sx={{ ...fieldSx(themeStyle), width: 160 }}
                                />
                            </Box>
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        Deliverable types
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Controls which deliverable types are available when brands create campaign deliverables.
                        Disabled types are hidden from the picker.
                    </Typography>
                    <Stack spacing={2}>
                        {deliverableTypes.map((dt, idx) => (
                            <Box
                                key={dt.id}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 1,
                                    borderBottom: `1px solid ${appTheme[themeStyle].form_divider}`,
                                }}>
                                <Box sx={{ flex: 1, minWidth: 140 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: appTheme[themeStyle].tag_white,
                                            fontSize: '0.9rem',
                                        }}>
                                        {dt.label}
                                    </Typography>
                                    {dt.platform && (
                                        <Typography sx={{ fontSize: '0.7rem', color: appTheme[themeStyle].grey }}>
                                            {dt.platform}
                                        </Typography>
                                    )}
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dt.enabled !== false}
                                            onChange={(_, checked) => {
                                                const next = [...deliverableTypes];
                                                next[idx] = { ...dt, enabled: checked };
                                                updateDeliverableTypes(next);
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: appTheme[themeStyle].tag_white }}>Enabled</Typography>
                                    }
                                />
                                <LabeledField label="Sort order" themeStyle={themeStyle}>
                                    <TextField
                                        value={dt.sort_order ?? idx}
                                        size="small"
                                        type="number"
                                        onChange={e => {
                                            const num = parseInt(e.target.value, 10);
                                            if (isNaN(num)) return;
                                            const next = [...deliverableTypes];
                                            next[idx] = { ...dt, sort_order: num };
                                            updateDeliverableTypes(next);
                                        }}
                                        sx={{ ...fieldSx(themeStyle), width: 90 }}
                                    />
                                </LabeledField>
                                <Tooltip title="Remove">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            updateDeliverableTypes(deliverableTypes.filter((_, i) => i !== idx))
                                        }
                                        sx={{ color: appTheme[themeStyle].grey, '&:hover': { color: '#ff6b6b' } }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ))}

                        {/* Add new deliverable type */}
                        <Box
                            sx={{
                                mt: 1,
                                p: 2,
                                borderRadius: 1,
                                border: `1px dashed ${appTheme[themeStyle].form_divider}`,
                            }}>
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: appTheme[themeStyle].tag_white,
                                    fontSize: '0.8rem',
                                    mb: 1.5,
                                }}>
                                Add new deliverable type
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
                                <LabeledField label="ID (snake_case)" themeStyle={themeStyle}>
                                    <TextField
                                        value={newDeliverable.id}
                                        size="small"
                                        placeholder="snapchat_story"
                                        onChange={e => {
                                            setNewDeliverableError('');
                                            setNewDeliverable(p => ({ ...p, id: e.target.value }));
                                        }}
                                        sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                    />
                                </LabeledField>
                                <LabeledField label="Label" themeStyle={themeStyle}>
                                    <TextField
                                        value={newDeliverable.label}
                                        size="small"
                                        placeholder="Snapchat Story"
                                        onChange={e => {
                                            setNewDeliverableError('');
                                            setNewDeliverable(p => ({ ...p, label: e.target.value }));
                                        }}
                                        sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                    />
                                </LabeledField>
                                <LabeledField label="Platform (optional)" themeStyle={themeStyle}>
                                    <TextField
                                        value={newDeliverable.platform}
                                        size="small"
                                        placeholder="snapchat"
                                        onChange={e => setNewDeliverable(p => ({ ...p, platform: e.target.value }))}
                                        sx={{ ...fieldSx(themeStyle), width: '100%' }}
                                    />
                                </LabeledField>
                            </Box>
                            {newDeliverableError && (
                                <Typography sx={{ fontSize: '0.75rem', color: '#ff6b6b', mb: 1 }}>
                                    {newDeliverableError}
                                </Typography>
                            )}
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addDeliverableType}
                                sx={{
                                    color: appTheme[themeStyle].highlight,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    border: `1px solid ${appTheme[themeStyle].highlight}`,
                                    px: 2,
                                    '&:hover': { bgcolor: `${appTheme[themeStyle].highlight}18` },
                                }}>
                                Add type
                            </Button>
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded sx={accordionSx(themeStyle)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: appTheme[themeStyle].tag_white }} />}>
                    <Typography variant="h6" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD }}>
                        Duration ranges
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            color: appTheme[themeStyle].grey,
                            fontSize: '0.75rem',
                            mb: 2,
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                        }}>
                        Controls which duration options appear in the deliverable picker. Disabled ranges are hidden.
                    </Typography>
                    <Stack spacing={2}>
                        {durationRanges.map((dr, idx) => (
                            <Box
                                key={dr.id}
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 2,
                                    py: 1,
                                    borderBottom: `1px solid ${appTheme[themeStyle].form_divider}`,
                                }}>
                                <Typography
                                    sx={{
                                        flex: 1,
                                        minWidth: 140,
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        color: appTheme[themeStyle].tag_white,
                                        fontSize: '0.9rem',
                                    }}>
                                    {dr.label}
                                    <Typography
                                        component="span"
                                        sx={{ ml: 1, fontSize: '0.7rem', color: appTheme[themeStyle].grey }}>
                                        ({dr.id})
                                    </Typography>
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={dr.enabled !== false}
                                            onChange={(_, checked) => {
                                                const next = [...durationRanges];
                                                next[idx] = { ...dr, enabled: checked };
                                                updateDurationRanges(next);
                                            }}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: appTheme[themeStyle].highlight,
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: appTheme[themeStyle].highlight,
                                                },
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography sx={{ color: appTheme[themeStyle].tag_white }}>Enabled</Typography>
                                    }
                                />
                                <LabeledField label="Sort order" themeStyle={themeStyle}>
                                    <TextField
                                        value={dr.sort_order ?? idx}
                                        size="small"
                                        type="number"
                                        onChange={e => {
                                            const num = parseInt(e.target.value, 10);
                                            if (isNaN(num)) return;
                                            const next = [...durationRanges];
                                            next[idx] = { ...dr, sort_order: num };
                                            updateDurationRanges(next);
                                        }}
                                        sx={{ ...fieldSx(themeStyle), width: 90 }}
                                    />
                                </LabeledField>
                            </Box>
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </>
    );
}

function PreEndReminderDaysField({
    value,
    onCommit,
    themeStyle,
}: {
    value: number[];
    onCommit: (days: number[]) => void;
    themeStyle: keyof typeof appTheme;
}) {
    const [draft, setDraft] = React.useState(value.join(', '));

    React.useEffect(() => {
        setDraft(value.join(', '));
    }, [value]);

    const commit = () => {
        const days = draft
            .split(/[,\s]+/)
            .map(s => parseInt(s.trim(), 10))
            .filter(n => Number.isFinite(n) && n > 0);
        const next = days.length ? days : [...DEFAULT_CAMPAIGN_DEADLINES.pre_end_reminder_days];
        onCommit(next);
        setDraft(next.join(', '));
    };

    return (
        <TextField
            size="small"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={e => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    commit();
                    (e.target as HTMLInputElement).blur();
                }
            }}
            placeholder="3, 1"
            sx={{ ...fieldSx(themeStyle), width: '100%' }}
        />
    );
}

function LabeledField({
    label,
    themeStyle,
    children,
}: {
    label: string;
    themeStyle: keyof typeof appTheme;
    children: React.ReactNode;
}) {
    return (
        <Box>
            <Typography
                sx={{
                    color: appTheme[themeStyle].tag_white,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: '0.8rem',
                    mb: 0.5,
                }}>
                {label}
            </Typography>
            {children}
        </Box>
    );
}
